import { get } from "lodash";
import {
  PolicyValidationView,
  normalizeValidationResults,
} from "@platform/policy-validation-ui";
import { useCallback, useContext, useEffect, useState } from "react";

import { ClientContext } from "../App";
import { executeAndPollPolicyAction, validatePolicy } from "../lib/automatic";
import { MAX_POLL_DURATION_MS, POLL_INTERVAL_MS } from "../lib/constants";
import PolicyActionConfirmModal from "./PolicyActionConfirmModal";

const callValidatePolicy = async (
  { client, policyId },
  clientId,
  accountId,
  policyTerm
) => {
  // Only get rules for this policy if validation rules are enabled on the client
  try {
    if (get(client, "config.validationConfig.enabled")) {
      const queryParameters =
        policyTerm === "renewal" ? { term: "renewal" } : {};
      return await validatePolicy(
        policyId,
        clientId,
        accountId,
        queryParameters
      );
    }
  } catch (e) {
    console.log(e);
  }
};

const PolicyValidation = ({
  title,
  iconStatus,
  client,
  policyId,
  accountId,
  validationActions = [],
  onPolicyRefreshSuccess,
  onValidationResults,
  isActionInProgress = false,
  validationTriggerRef,
  policyTerm = "current",
}) => {
  const [loading, setLoading] = useState(true);
  const [validationResults, setValidationResults] = useState([]);
  const [revalidateKey, setRevalidateKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const { selectedClient: clientId } = useContext(ClientContext);

  // Extract validation logic
  const performValidation = useCallback(async () => {
    setLoading(true);
    try {
      const data = await callValidatePolicy(
        { client, policyId },
        clientId,
        accountId,
        policyTerm
      );
      if (data) {
        const normalized = normalizeValidationResults(data);
        setValidationResults(normalized);
        // Notify parent of validation results so other components can react
        if (onValidationResults) {
          onValidationResults(get(normalized, "[0].result.rules", {}));
        }
      }
    } catch (error) {
      console.error("Validation error:", error);
    } finally {
      setLoading(false);
    }
  }, [client, policyId, clientId, accountId, onValidationResults, policyTerm]);

  // Update policy data (hot loading) and trigger revalidation
  // Revalidation is handled by the parent via validationTriggerRef
  const handlePolicyRefreshSuccess = useCallback(
    (updatedPolicy) => {
      // Hot load + revalidation handled by parent callback
      if (onPolicyRefreshSuccess && updatedPolicy) {
        onPolicyRefreshSuccess(updatedPolicy);
      }
    },
    [onPolicyRefreshSuccess]
  );

  // Expose validation trigger function via ref so parent can trigger revalidation

  useEffect(() => {
    if (validationTriggerRef) {
      validationTriggerRef.current = () => {
        setRevalidateKey((prev) => prev + 1);
      };
    }
    return () => {
      if (validationTriggerRef) {
        validationTriggerRef.current = null;
      }
    };
  }, [validationTriggerRef]);

  // Run validation on mount, when policyId/clientId/term changes, or when revalidateKey changes
  useEffect(() => {
    performValidation();
  }, [revalidateKey, policyId, clientId, policyTerm, performValidation]);

  const handleActionClick = (action) => {
    if (isActionInProgress) return;
    setSelectedAction(action);
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    await executeAndPollPolicyAction(
      policyId,
      selectedAction.type,
      clientId,
      POLL_INTERVAL_MS,
      MAX_POLL_DURATION_MS,
      handlePolicyRefreshSuccess,
      policyTerm === "renewal" ? { term: "renewal" } : {}
    );
  };

  return (
    <>
      <PolicyValidationView
        title={title}
        iconStatus={iconStatus}
        loading={loading}
        validationResults={validationResults}
        validationActions={validationActions}
        onPolicyActionConfirm={handleActionClick}
        isActionInProgress={isActionInProgress}
      />
      <PolicyActionConfirmModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        action={selectedAction}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default PolicyValidation;
